import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { getUnreadInquiriesV2QueryOptions } from '@/services/getUnreadInquiriesV2';
import { userIdAtom } from '@/services/globalState';
import { shouldFetchUserScoped } from '@/util/authTokens';

/**
 * Hook to access unread inquiry counts from the optimized API.
 * Uses React Query cache as the single source of truth.
 * Invalidated automatically by the inbox sync on message events.
 */
export function useUnreadCounts(cancerTypeId?: number) {
  const userId = useAtomValue(userIdAtom);

  const { data, isLoading } = useQuery({
    ...getUnreadInquiriesV2QueryOptions(userId, cancerTypeId),
    enabled: shouldFetchUserScoped(userId),
  });

  const unreadByTab = useMemo(
    () => data?.unreadByTab ?? DEFAULT_UNREAD_BY_TAB,
    [data?.unreadByTab]
  );

  return useMemo(
    () => ({
      hasAnyUnread: data?.hasAnyUnread ?? false,
      unreadConversationsCount: data?.unreadConversationsCount ?? 0,
      unreadByTab,
      isLoading,
    }),
    [data?.hasAnyUnread, data?.unreadConversationsCount, unreadByTab, isLoading]
  );
}

const DEFAULT_UNREAD_BY_TAB = Object.freeze({ all: 0, open: 0, closed: 0 });
